<template>
  <fullscreen-dialog
    v-model="model"
    :title="dialogTitle"
    icon="mdi-clipboard-check-outline"
    color="deep-purple"
  >
    <template #app-bar-actions>
      <excel-button @click="exportExcel" class="mr-2"></excel-button>
      <print-button @click="print"></print-button>
    </template>

    <template #footer-actions>
      <v-btn
        color="deep-purple"
        :disabled="!hasChanges"
        :loading="savingAll"
        :dark="hasChanges"
        depressed
        large
        @click="saveAll"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ $t("save_all") }}
      </v-btn>
      <v-btn
        color="success"
        :disabled="!hasSavedItems || completing"
        :loading="completing"
        :dark="hasSavedItems && completing"
        depressed
        large
        @click="completeStocktaking"
        class="ml-2"
      >
        <v-icon left>mdi-checkbox-marked-circle-outline</v-icon>
        {{ $t("complete_stocktaking") }}
      </v-btn>
    </template>

    <v-form ref="form">
      <v-row>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="pb-0">
          <text-field
            :value="editedItem.warehouse?.name || ''"
            :label="$t('stock')"
            readonly
            hide-details
            color="white"
          ></text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="pb-0">
          <text-field
            :value="localeDate(editedItem.date)"
            :label="$t('date')"
            readonly
            hide-details
            color="white"
          ></text-field>
        </v-col>
      </v-row>
    </v-form>

    <div
      v-if="editedItem.note"
      class="mt-8 px-4 py-3 grey--text text--darken-3 body-1"
      style="
        background-color: #e3f2fd;
        border-left: 4px solid #1976d2;
        border-radius: 4px;
      "
    >
      {{ editedItem.note }}
    </div>

    <warehouse-initial-state-table
      ref="groceryTable"
      :groceries="internalGroceries"
      :warehouse="editedItem.warehouse"
      :loading="loadingItems"
      :is-completed="false"
      @removeGrocery="removeGrocery"
      @saveItem="saveItem"
      @addGrocery="openAddGroceryDialog"
      class="mt-4"
      :key="editedItem.warehouse ? editedItem.warehouse.id : 0"
    >
    </warehouse-initial-state-table>
    <grocery-dialog
      v-model="groceryDialog"
      :edited-item="editedGrocery"
      @groceryCreated="onGroceryCreated"
    />
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasExcel from "@/mixins/hasExcel";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import WarehouseInitialStateTable from "@/components/warehouse/initialState/WarehouseInitialStateTable.vue";
import GroceryDialog from "@/components/groceries/dialogs/GroceryDialog.vue";
import { defaultGrocery } from "@/models/food";
import warehouseService from "@/services/warehouse";
export default {
  name: "StocktakingDialog",
  mixins: [hasModel, hasPrinting, hasSnackbar, hasExcel],
  components: {
    FullscreenDialog,
    WarehouseInitialStateTable,
    GroceryDialog,
  },
  props: {
    listId: {
      type: [Number, String],
      default: null,
    },
    initialDocuments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loadingItems: false,
      internalGroceries: [],
      savingAll: false,
      completing: false,
      stocktakingDetails: null,
      groceryDialog: false,
      editedGrocery: { ...defaultGrocery },
      editedItem: {
        warehouse: null,
        date: "",
        name: "",
        groceries: [],
        documents: [],
      },
    };
  },
  methods: {
    /**
     * Main entry point for loading active stocktaking data
     */
    async loadWarehouseInventory() {
      if (!this.listId) return;

      try {
        this.loadingItems = true;
        await this.fetchStocktakingDetails();
        await this.loadActiveStocktaking();
      } catch (error) {
        console.error("Error loading warehouse inventory:", error);
        this.showErrorSnackbar(this.$t("error_loading_inventory"));
      } finally {
        this.loadingItems = false;
      }
    },

    /**
     * Fetch stocktaking details from API
     */
    async fetchStocktakingDetails() {
      const detailsResponse = await warehouseService.getStocktakingDetails(this.listId);
      const details = detailsResponse.data?.data || detailsResponse.data || null;

      this.editedItem = { ...details };
      this.stocktakingDetails = this.editedItem;
    },

    /**
     * Load groceries for active stocktaking
     * Shows all groceries, merging with counted data
     */
    async loadActiveStocktaking() {
      const allGroceriesFromList = this.editedItem.groceries || [];

      // Filter only groceries that have been ACTUALLY counted (have physical_stock)
      const countedGroceries = allGroceriesFromList.filter(
        (g) => g.physical_stock !== null && g.physical_stock !== undefined
      );
      const countedMap = new Map(countedGroceries.map((g) => [g.grocery_id || g.id, g]));

      if (!this.editedItem.warehouse) return;
      const statusResponse = await warehouseService.getWarehouseStatus({
        warehouse_ids: this.editedItem.warehouse.id,
        date: new Date().toISOString().split("T")[0],
      });
      const warehouses = statusResponse.data?.data || statusResponse.data || [];

      // Find current warehouse in the response
      const currentWarehouse = warehouses.find(
        (w) => w.id === this.editedItem.warehouse.id
      );

      const allGroceries = currentWarehouse?.groceries || [];

      this.internalGroceries = allGroceries.map((grocery) =>
        this.mapActiveGrocery(grocery, countedMap)
      );
    },

    /**
     * Map a grocery item for active stocktaking
     * Merges grocery data with counted data if available
     */
    mapActiveGrocery(grocery, countedMap) {
      const groceryId = grocery.id;
      const countedItem = countedMap.get(groceryId);

      if (countedItem) {
        // Grocery has been counted
        return {
          id: groceryId,
          code: grocery.code,
          name: grocery.name,
          measure: grocery.measure,
          current_quantity: parseFloat(countedItem.system_stock) || 0,
          new_quantity: parseFloat(countedItem.physical_stock) || 0,
          quantity: parseFloat(countedItem.difference) || 0,
          counted: true,
          inWarehouse: true,
        };
      }

      // Grocery not yet counted
      const currentStock = parseFloat(grocery.total || grocery.quantity) || 0;
      const hasStock = currentStock > 0;

      return {
        id: groceryId,
        code: grocery.code,
        name: grocery.name,
        measure: grocery.measure,
        current_quantity: hasStock ? currentStock : null,
        new_quantity: null, // User needs to enter physical stock
        quantity: 0,
        counted: false,
        inWarehouse: hasStock,
      };
    },
    removeGrocery(grocery) {
      this.internalGroceries = this.internalGroceries.filter(
        (item) => grocery.id !== item.id
      );
    },
    openAddGroceryDialog() {
      this.editedGrocery = { ...defaultGrocery };
      this.groceryDialog = true;
    },
    onGroceryCreated(grocery) {
      // Add the new grocery to the list with initial values
      this.internalGroceries.push({
        id: grocery.id,
        code: grocery.code,
        name: grocery.name,
        measure: grocery.measure,
        current_quantity: 0,
        new_quantity: null,
        quantity: 0,
        counted: false,
        inWarehouse: false,
      });
      this.groceryDialog = false;
    },
    async saveItem(item) {
      try {
        this.$set(item, "saving", true);

        await warehouseService.saveStocktakingGrocery({
          warehouse_lists_id: this.editedItem.id,
          grocery_id: item.id,
          physical_stock: item.new_quantity,
          warehouse_id: this.editedItem.warehouse.id,
        });

        this.$set(item, "counted", true);
        this.$set(item, "focused", false);
        this.showSuccessSnackbar(this.$t("saved_successfully"));
      } catch (error) {
        console.error("Error saving stocktaking item:", error);
        this.showErrorSnackbar(this.$t("error_saving"));
      } finally {
        this.$set(item, "saving", false);
      }
    },
    async saveAll() {
      this.savingAll = true;
      try {
        // Only save items that have a valid new_quantity (not null, undefined, or empty)
        const itemsToSave = this.internalGroceries.filter(
          (item) =>
            !item.counted &&
            item.new_quantity !== null &&
            item.new_quantity !== undefined &&
            item.new_quantity !== "" &&
            item.new_quantity >= 0
        );

        for (const item of itemsToSave) {
          this.$set(item, "saving", true);

          await warehouseService.saveStocktakingGrocery({
            warehouse_lists_id: this.editedItem.id,
            grocery_id: item.id,
            physical_stock: item.new_quantity,
            warehouse_id: this.editedItem.warehouse.id,
          });

          this.$set(item, "counted", true);
          this.$set(item, "saving", false);
        }

        this.showSuccessSnackbar(this.$t("saved_successfully"));
      } catch (error) {
        console.error("Error saving all stocktaking items:", error);
        this.showErrorSnackbar(this.$t("error_saving"));
      } finally {
        this.savingAll = false;
      }
    },
    async completeStocktaking() {
      this.completing = true;
      try {
        await warehouseService.completeList({
          warehouse_lists_id: this.editedItem.id,
        });

        this.showSuccessSnackbar(this.$t("stocktaking_completed_successfully"));
        this.$emit("completed");
        this.model = false;
      } catch (error) {
        console.error("Error completing stocktaking:", error);
        this.showErrorSnackbar(this.$t("error_completing_stocktaking"));
      } finally {
        this.completing = false;
      }
    },
    print() {
      const countedItems = this.internalGroceries;
      const items = countedItems.map((item, index) => ({
        num: index + 1,
        code: item.code || "",
        name: item.name,
        current_quantity: item.counted
          ? parseFloat(item.current_quantity || 0).toFixed(2)
          : "",
        new_quantity: item.counted ? parseFloat(item.new_quantity || 0).toFixed(2) : "",
        quantity: item.counted ? parseFloat(item.quantity || 0).toFixed(2) : "",
        measure: item.measure,
      }));

      this.printDocument({
        tables: [{ headers: this.printHeaders, items }],
        title: this.$t("stocktaking") + " - " + (this.editedItem.name || ""),
        beforeTitleHtml: `
          <h2 style="margin-bottom: 0px">${this.$t("stock")}: ${this.toLatin(
          this.editedItem.warehouse?.name || ""
        )}</h2>
          <div style="font-size: 16px;margin-top: 20px;">
            ${this.$t("date")}: ${this.editedItem.date || ""}
          </div>
        `,
        hideDatetime: true,
      });
    },
    exportExcel() {
      const countedItems = this.internalGroceries;
      const items = countedItems.map((item, index) => ({
        num: index + 1,
        code: item.code || "",
        name: item.name,
        current_quantity: item.counted
          ? parseFloat(item.current_quantity || 0).toFixed(2)
          : "",
        new_quantity: item.counted ? parseFloat(item.new_quantity || 0).toFixed(2) : "",
        quantity: item.counted ? parseFloat(item.quantity || 0).toFixed(2) : "",
        measure: item.measure,
      }));

      const warehouseName = this.toLatin(this.editedItem.warehouse?.name || "Magacin");
      const stocktakingName = this.toLatin(this.editedItem.name || "Popis");

      this.downloadExcel({
        fileName: `${stocktakingName}_${this.editedItem.date || ""}.xlsx`,
        sheets: [
          {
            name: this.$t("stocktaking"),
            headers: this.printHeaders,
            items,
            title: `${this.$t("stocktaking")} - ${stocktakingName}`,
            subtitle: `${this.$t("stock")}: ${warehouseName} | ${this.$t("date")}: ${
              this.editedItem.date || ""
            }`,
          },
        ],
      });
    },
  },
  computed: {
    dialogTitle() {
      return (
        this.$t("stocktaking") +
        " - " +
        (this.editedItem.name || "") +
        " " +
        this.localeDate(this.editedItem.date)
      );
    },
    hasUncountedItems() {
      return this.internalGroceries.some((item) => !item.counted);
    },
    hasChanges() {
      // Check if there are uncounted items with valid new_quantity to save
      return this.internalGroceries.some(
        (item) =>
          !item.counted &&
          item.new_quantity !== null &&
          item.new_quantity !== undefined &&
          item.new_quantity !== "" &&
          item.new_quantity >= 0
      );
    },
    hasSavedItems() {
      // Check if there is at least one counted (saved) item
      return this.internalGroceries.some((item) => item.counted);
    },
    printHeaders() {
      return [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("code"), value: "code" },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("book_state"), value: "current_quantity" },
        { text: this.$t("inventory_state"), value: "new_quantity" },
        { text: this.$t("difference"), value: "quantity" },
        { text: this.$t("unit_of_measurment"), value: "measure" },
      ];
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.internalGroceries = [];
        this.loadWarehouseInventory();
      } else {
        // Reset data when dialog closes
        this.editedItem = {
          warehouse: null,
          date: "",
          name: "",
          groceries: [],
        };
        this.stocktakingDetails = null;
      }
    },
    listId(newVal) {
      if (newVal && this.model) {
        this.internalGroceries = [];
        this.loadWarehouseInventory();
      }
    },
  },
};
</script>
